import { apiErrorMessage, rememberApiError } from '@bayesprep/shared-ui/errors';
import axios from 'axios';
import { confirmSessionFailure } from '../lib/sessionFailure';

// Always use relative URLs to work with nginx reverse proxy
// This ensures all requests go through: nginx → API Gateway → microservices
const API_BASE_URL = window.location.origin;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and user ID
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // For development, check if it's a dev token
      if (process.env.NODE_ENV === 'development' && token.startsWith('eyJ')) {
        // It's a base64 encoded dev token, use it as is
        config.headers.Authorization = `Bearer ${token}`;
      } else if (process.env.NODE_ENV === 'development') {
        // It's a dev token, use it as is
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        // Production token
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Add X-User-ID header for Content Team and Super Admin endpoints
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail && (
      config.url?.includes('/content-generator/') ||
      config.url?.includes('/content-reviewer/') ||
      config.url?.includes('/content-supervisor/') ||
      config.url?.includes('/super-admin/')
    )) {
      config.headers['X-User-ID'] = userEmail;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async error => {
    let sessionRetained = false;
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/')) {
      const header = error.config?.headers?.Authorization;
      const token = typeof header === 'string' ? header.replace(/^Bearer /, '') : null;
      sessionRetained = !await confirmSessionFailure(token);
    }
    return Promise.reject(rememberApiError(error, sessionRetained));
  }
);



// Super Admin API endpoints
export const superAdminAPI = {
  // Dashboard
  getSystemHealth: () => apiClient.get('/api/super-admin/health/system'),
  getQuickActions: () => apiClient.get('/api/super-admin/dashboard/quick-actions'),
  getNavigation: () => apiClient.get('/api/super-admin/dashboard/navigation'),

  // Tenant Management
  getAllTenants: (params = {}) => apiClient.get('/api/super-admin/tenants', { params }),
  getTenantById: (id) => apiClient.get(`/api/super-admin/tenants/${id}`),
  createTenant: (tenantData) => apiClient.post('/api/super-admin/tenants', tenantData),
  updateTenantStatus: (id, status) => apiClient.patch(`/api/super-admin/tenants/${id}/status`, null, { params: { status } }),
  updateTenant: (id, tenantData) => apiClient.put(`/api/super-admin/tenants/${id}`, tenantData),
  deleteTenant: (id) => apiClient.delete(`/api/super-admin/tenants/${id}`),
  toggleTenantStatus: (id) => apiClient.patch(`/api/super-admin/tenants/${id}/toggle-status`),
  getTenantDashboardStats: () => apiClient.get('/api/super-admin/tenants/stats'),
  getTenantUsageStats: (id) => apiClient.get(`/api/super-admin/tenants/${id}/usage-stats`),
  updateTenantSubscription: (id, subscriptionData) =>
    apiClient.put(`/api/super-admin/tenants/${id}/subscription`, subscriptionData),
  provisionAdminAccess: (tenantId, userData) =>
    apiClient.post(`/api/super-admin/tenants/${tenantId}/provision-admin`, userData),
  revokeAdminAccess: (tenantId, userData) =>
    apiClient.delete(`/api/super-admin/tenants/${tenantId}/revoke-admin`, { data: userData }),

  // User Management
  searchUsers: (params = {}) => apiClient.get('/api/super-admin/users/search', { params }),
  resendContentSetupEmail: id => apiClient.post(`/api/super-admin/users/${encodeURIComponent(id)}/setup-email`),
  sendContentPasswordReset: id => apiClient.post(`/api/super-admin/users/${encodeURIComponent(id)}/content-password-reset`),
  updateContentProfile: (id, profile) => apiClient.put(`/api/super-admin/users/${encodeURIComponent(id)}/content-profile`, profile),
  updatePlatformProfile: (id, profile) => apiClient.put(`/api/super-admin/users/${encodeURIComponent(id)}/platform-profile`, profile),
  sendPlatformPasswordReset: id => apiClient.post(`/api/super-admin/users/${encodeURIComponent(id)}/platform-password-reset`),
  getUserById: (id) => apiClient.get(`/api/super-admin/users/${id}`),
  resetUserPassword: (id, passwordData) =>
    apiClient.post(`/api/super-admin/users/${id}/reset-password`, passwordData),
  updateUserRoles: (id, roleData) =>
    apiClient.put(`/api/super-admin/users/${id}/roles`, roleData),
  updateUserStatus: (id, statusData) =>
    apiClient.put(`/api/super-admin/users/${id}/status`, statusData),
  deleteUser: (id) => apiClient.delete(`/api/super-admin/users/${id}`),
  getUserStatistics: () => apiClient.get('/api/super-admin/users/statistics'),

  // Admin Management
  createAdmin: (adminData) => apiClient.post('/api/super-admin/admins', adminData),
  updateAdmin: (id, adminData) => apiClient.put(`/api/super-admin/admins/${id}`, adminData),
  sendAdminWelcomeEmail: (emailData) =>
    apiClient.post('/api/super-admin/admins/send-welcome-email', emailData),
  resendAdminWelcomeEmail: (id, emailData) =>
    apiClient.post(`/api/super-admin/admins/${id}/resend-welcome-email`, emailData),

  // Question Management
  getAllQuestions: (params = {}) => apiClient.get('/api/super-admin/questions', { params }),
  getQuestionById: (id) => apiClient.get(`/api/super-admin/questions/by-id/${id}`),
  createQuestion: (questionData) => apiClient.post('/api/super-admin/questions', questionData),
  updateQuestion: (id, questionData) => apiClient.put(`/api/super-admin/questions/${id}`, questionData),
  deleteQuestion: (id) => apiClient.delete(`/api/super-admin/questions/${id}`),

  // For question creation/editing forms (database + predefined values)
  getQuestionSubjects: (params = {}) => apiClient.get('/api/super-admin/questions/subjects', { params }),
  getQuestionTopics: (subject, params = {}) => apiClient.get(`/api/super-admin/questions/topics?subject=${subject}`, { params }),
  getQuestionExamTypes: (params = {}) => apiClient.get('/api/super-admin/questions/exam-types', { params }),

  // For filtering (database values only)
  searchQuestionSubjects: (params = {}) => apiClient.get('/api/super-admin/questions/subjects/search', { params }),
  searchQuestionTopics: (subject, params = {}) => {
    const queryParams = new URLSearchParams(params);
    if (subject) queryParams.append('subject', subject);
    return apiClient.get(`/api/super-admin/questions/topics/search?${queryParams.toString()}`);
  },
  searchQuestionExamTypes: (params = {}) => apiClient.get('/api/super-admin/questions/exam-types/search', { params }),
  searchQuestionDifficultyLevels: (params = {}) => apiClient.get('/api/super-admin/questions/difficulty-levels/search', { params }),
  searchQuestionTypes: (params = {}) => apiClient.get('/api/super-admin/questions/question-types/search', { params }),

  getQuestionSubtopics: (subject, topic, params = {}) => {
    const queryParams = new URLSearchParams(params);
    queryParams.append('subject', subject);
    if (topic) queryParams.append('topic', topic);
    return apiClient.get(`/api/super-admin/questions/subtopics?${queryParams.toString()}`);
  },
  getQuestionExamNames: (params = {}) => apiClient.get('/api/super-admin/questions/exam-names', { params }),
  getQuestionDifficultyLevels: (params = {}) => apiClient.get('/api/super-admin/questions/difficulty-levels', { params }),
  getQuestionTypes: (params = {}) => apiClient.get('/api/super-admin/questions/question-types', { params }),
  getSupportedQuestionTypes: () => apiClient.get('/api/super-admin/questions/supported-types'),
  getExcelTemplate: (questionType = null) => {
    const params = questionType ? { questionType } : {};
    return apiClient.get('/api/super-admin/questions/excel-template', {
      params,
      responseType: 'blob'
    });
  },
  bulkUploadQuestions: (formData) =>
    apiClient.post('/api/super-admin/questions/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  validateQuestionsFile: (formData) =>
    apiClient.post('/api/super-admin/questions/validate-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  downloadQuestionsErrorReport: (formData) =>
    apiClient.post('/api/super-admin/questions/download-error-report', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob'
    }),
  getBulkUploadTemplate: () => apiClient.get('/api/super-admin/questions/excel-template'),
  batchAssignQuestions: (assignmentData) =>
    apiClient.post('/api/super-admin/questions/batch-assign', assignmentData),
  getQuestionStatistics: () => apiClient.get('/api/super-admin/questions/stats'),

  // Image Management
  uploadImage: (formData) => {
    formData.set('scope', 'global');
    return apiClient.post('/api/core/images/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteImage: (imageUrl) => apiClient.delete('/api/core/images', { params: { imageUrl } }),
  bulkUploadImages: (formData) => {
    formData.set('scope', 'global');
    return apiClient.post('/api/core/images/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateQuestionImages: (questionId, imageUrls) =>
    apiClient.post(`/api/super-admin/images/questions/${questionId}/images`, imageUrls),

  // Draft Question Management
  getAllDraftQuestions: (params = {}) => apiClient.get('/api/super-admin/draft-questions', { params }),
  getDraftQuestionById: (id) => apiClient.get(`/api/super-admin/draft-questions/${id}`),
  createDraftQuestion: (questionData) => apiClient.post('/api/super-admin/draft-questions', questionData),
  updateDraftQuestion: (id, questionData) => apiClient.put(`/api/super-admin/draft-questions/${id}`, questionData),
  deleteDraftQuestion: (id) => apiClient.delete(`/api/super-admin/draft-questions/${id}`),
  reviewDraftQuestion: (id, reviewData) => apiClient.post(`/api/super-admin/draft-questions/${id}/review`, reviewData),
  publishDraftQuestion: (id) => apiClient.post(`/api/super-admin/draft-questions/${id}/publish`),
  getDraftQuestionsForReview: (params = {}) => apiClient.get('/api/super-admin/draft-questions/for-review', { params }),
  getDraftQuestionsForPublishing: (params = {}) => apiClient.get('/api/super-admin/draft-questions/for-publishing', { params }),
  getDraftQuestionStats: () => apiClient.get('/api/super-admin/draft-questions/stats'),
  getDraftQuestionSubjects: () => apiClient.get('/api/super-admin/draft-questions/subjects'),
  getDraftQuestionTopics: (subject) => apiClient.get(`/api/super-admin/draft-questions/topics?subject=${subject}`),
  bulkUploadDraftQuestions: (formData) =>
    apiClient.post('/api/super-admin/draft-questions/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  validateDraftQuestionsFile: (formData) =>
    apiClient.post('/api/super-admin/draft-questions/validate-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  downloadDraftQuestionsErrorReport: (formData) =>
    apiClient.post('/api/super-admin/draft-questions/download-error-report', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob'
    }),



  // Content Role Management
  createContentSupervisor: (userData) => apiClient.post('/api/super-admin/admins/content-supervisor', userData),
  createContentGenerator: (userData) => apiClient.post('/api/super-admin/admins/content-generator', userData),
  createContentReviewer: (userData) => apiClient.post('/api/super-admin/admins/content-reviewer', userData),

  uploadAuthorImage: (formData) => apiClient.post('/api/super-admin/content-generator/images/upload', formData, {headers:{'Content-Type':'multipart/form-data'}}),

  searchAssignableUsers: (params) => apiClient.get('/api/super-admin/users/search', {params}),
  confirmLegacyAssignment: id => apiClient.post(`/api/super-admin/draft-questions/${id}/confirm-assignment`),
  assignDraftWork: (id, data) => apiClient.post(`/api/super-admin/draft-questions/${id}/assign`, data),

  // Content Generator API endpoints
  getContentGeneratorStats: () => apiClient.get('/api/super-admin/content-generator/stats'),
  getContentGeneratorKits: () => apiClient.get('/api/super-admin/content-generator/kits'),
  getContentGeneratorQuestions: (params = {}) => apiClient.get('/api/super-admin/content-generator/my-questions', { params }),
  createContentGeneratorQuestion: (questionData) => apiClient.post('/api/super-admin/content-tasks', questionData),
  updateContentGeneratorQuestion: (id, questionData) => apiClient.put(`/api/super-admin/content-tasks/${id}`, questionData),
  deleteContentGeneratorQuestion: (id) => apiClient.delete(`/api/super-admin/content-generator/questions/${id}`),
  submitQuestionForReview: (id) => apiClient.post(`/api/super-admin/content-generator/questions/${id}/submit-for-review`),
  getAssignedQuestions: (params = {}) => apiClient.get('/api/super-admin/content-generator/assigned-questions', { params }),
  generateKitDeletionRequest: (requestData) => apiClient.post('/api/super-admin/content-generator/kit-deletion-request', requestData),
  bulkUploadContentGeneratorQuestions: (formData) =>
    apiClient.post('/api/super-admin/content-generator/questions/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  downloadQuestionTemplate: (questionType = 'MCQ_SINGLE_CORRECT') => apiClient.get('/api/super-admin/content-generator/questions/template', { params: {questionType}, responseType: 'blob' }),
  validateAuthorQuestionsFile: (formData) => apiClient.post('/api/super-admin/content-generator/questions/validate-file', formData, {headers: {'Content-Type': 'multipart/form-data'}}),
  getContentGeneratorQuestionSubjects: () => apiClient.get('/api/super-admin/content-generator/questions/subjects'),
  getContentGeneratorQuestionTopics: (subject) => apiClient.get(`/api/super-admin/content-generator/questions/topics?subject=${subject}`),
  getContentGeneratorQuestionSubtopics: (subject, topic) => {
    const params = new URLSearchParams();
    if (subject) params.append('subject', subject);
    if (topic) params.append('topic', topic);
    return apiClient.get(`/api/super-admin/content-generator/questions/subtopics?${params.toString()}`);
  },
  getAuthorTagSummary: () => apiClient.get('/api/super-admin/content-generator/questions/tag-stats'),
  getContentGeneratorQuestionTags: () => apiClient.get('/api/super-admin/content-generator/questions/tags'),

  // Content Reviewer API endpoints
  getContentReviewerStats: () => apiClient.get('/api/super-admin/content-reviewer/stats'),
  getQuestionsForReview: (params = {}) => apiClient.get('/api/super-admin/content-reviewer/questions-for-review', { params }),
  getCompletedReviews: (params = {}) => apiClient.get('/api/super-admin/content-reviewer/completed-reviews', { params }),
  getReviewerAssignedQuestions: (params = {}) => apiClient.get('/api/super-admin/content-reviewer/assigned-questions', { params }),
  reviewQuestion: (id, reviewData) => apiClient.post(`/api/super-admin/content-reviewer/questions/${id}/status`, { status: reviewData.status, reviewComments: [reviewData.feedback, reviewData.suggestedChanges].filter(Boolean).join('\n') }),
  bulkReviewQuestions: (reviewData) => apiClient.post('/api/super-admin/content-reviewer/questions/bulk-review', reviewData),
  getQuestionReviewHistory: (id) => apiClient.get(`/api/super-admin/content-reviewer/questions/${id}/review-history`),
  informSupervisorReviewComplete: (completionData) => apiClient.post('/api/super-admin/content-reviewer/review-complete', completionData),
  bulkUpdateQuestionStatuses: (updateData) => apiClient.post('/api/super-admin/content-reviewer/questions/bulk-status-update', updateData),

  // Content Supervisor API endpoints
  getContentSupervisorStats: () => apiClient.get('/api/super-admin/content-supervisor/stats'),
  getActiveAssignments: (params = {}) => apiClient.get('/api/super-admin/content-supervisor/assignments', { params }),
  getPendingQuestions: (params = {}) => apiClient.get('/api/super-admin/content-supervisor/pending-questions', { params }),
  getContentSupervisorKits: (params = {}) => apiClient.get('/api/super-admin/content-supervisor/kits', { params }),
  getQuestionsForPublishing: (params = {}) => apiClient.get('/api/super-admin/content-supervisor/questions-for-publishing', { params }),
  publishQuestion: (id) => apiClient.post(`/api/super-admin/content-supervisor/questions/${id}/publish`),
  publishQuestions: (publishData) => apiClient.post('/api/super-admin/content-supervisor/questions/bulk-publish', publishData),
  bulkPublishQuestions: (publishData) => apiClient.post('/api/super-admin/content-supervisor/questions/bulk-publish', publishData),
  getTeamMembers: (params = {}) => apiClient.get('/api/super-admin/content-supervisor/team', { params }),

  // Work Assignment API endpoints
  createWorkAssignment: (assignmentData) => apiClient.post('/api/super-admin/content-supervisor/assignments', assignmentData),
  updateWorkAssignment: (id, assignmentData) => apiClient.put(`/api/super-admin/content-supervisor/assignments/${id}`, assignmentData),
  deleteWorkAssignment: (id) => apiClient.delete(`/api/super-admin/content-supervisor/assignments/${id}`),
  getUnassignedQuestions: (params = {}) => apiClient.get('/api/super-admin/content-supervisor/unassigned-questions', { params }),
  getContentGenerators: (params = {}) => apiClient.get('/api/super-admin/content-supervisor/content-generators', { params }),
  getContentReviewers: (params = {}) => apiClient.get('/api/super-admin/content-supervisor/content-reviewers', { params }),
  getQuestionsByIds: async (questionIds) => {
    const responses = await Promise.all(questionIds.map(id => apiClient.get(`/api/super-admin/draft-questions/${id}`)));
    return { data: responses.map(response => response.data) };
  },
  assignQuestionsToReviewer: (assignmentData) => apiClient.post('/api/super-admin/content-supervisor/assign-questions', assignmentData),
  getWorkflowAnalytics: (params = {}) => apiClient.get('/api/super-admin/content-supervisor/workflow-analytics', { params }),
  createContentSupervisorKit: (kitData) => apiClient.post('/api/super-admin/content-supervisor/kits', kitData),
  updateContentSupervisorKit: (id, kitData) => apiClient.put(`/api/super-admin/content-supervisor/kits/${id}`, kitData),
  archiveKit: (id, archiveData) => apiClient.post(`/api/super-admin/content-supervisor/kits/${id}/archive`, archiveData),
  getQuestionsWithIssues: (params = {}) => apiClient.get('/api/super-admin/content-supervisor/questions-with-issues', { params }),
  resolveQuestionIssue: (id, resolutionData) => apiClient.post(`/api/super-admin/content-supervisor/questions/${id}/resolve-issue`, resolutionData),

  // Exam Type Management
  getAllExamTypes: () => apiClient.get('/api/super-admin/exam-types'),
  getExamTypeById: (id) => apiClient.get(`/api/super-admin/exam-types/${id}`),
  createExamType: (examTypeData) => apiClient.post('/api/super-admin/exam-types', examTypeData),
  updateExamType: (id, examTypeData) =>
    apiClient.put(`/api/super-admin/exam-types/${id}`, examTypeData),
  deleteExamType: (id) => apiClient.delete(`/api/super-admin/exam-types/${id}`),
  toggleExamTypeStatus: (id) =>
    apiClient.patch(`/api/super-admin/exam-types/${id}/toggle-status`),
  getExamConfigurations: () => apiClient.get('/api/super-admin/exam-types/configurations'),
  createExamConfiguration: (configData) =>
    apiClient.post('/api/super-admin/exam-types/configurations', configData),
  getExamTypeStatistics: () => apiClient.get('/api/super-admin/exam-types/statistics'),

  // Kit Management
  getAllKits: (params = {}) => apiClient.get('/api/super-admin/kits', { params }),
  getActiveKits: (tenantId = null) => apiClient.get('/api/super-admin/kits/active', {
    params: tenantId ? { tenantId } : {}
  }),
  getKitById: (id) => apiClient.get(`/api/super-admin/kits/${id}`),
  getKitQuestions: (id, params = {}) => apiClient.get(`/api/super-admin/kits/${id}/questions`, { params }),
  createKit: (kitData) => apiClient.post('/api/super-admin/kits', kitData),
  updateKit: (id, kitData) => apiClient.put(`/api/super-admin/kits/${id}`, kitData),
  deleteKit: (id) => apiClient.delete(`/api/super-admin/kits/${id}`),
  toggleKitStatus: (id) => apiClient.patch(`/api/super-admin/kits/${id}/toggle-status`),
  getKitStatistics: () => apiClient.get('/api/super-admin/kits/stats'),
  getKitsByTenant: (tenantId) => apiClient.get(`/api/super-admin/kits/tenant/${tenantId}`),
  getKitNames: (params = {}) => apiClient.get('/api/super-admin/kits/names', { params }),
  updateKitTags: (id, tagData) => apiClient.put(`/api/super-admin/kits/${id}/tags`, tagData),

  // Tag Management
  getAllTags: (params = {}) => apiClient.get('/api/super-admin/tags', { params }),
  getTagsByCategory: () => apiClient.get('/api/super-admin/tags/categories'),
  getQuestionsByTags: (tags, params = {}) => apiClient.post('/api/super-admin/tags/questions', { tags, ...params }),
  createTag: (tagData) => apiClient.post('/api/super-admin/tags', tagData),
  deleteTag: (tagName) => apiClient.delete(`/api/super-admin/tags/${encodeURIComponent(tagName)}`),
  renameTag: (oldTagName, newTagData) => apiClient.put(`/api/super-admin/tags/${encodeURIComponent(oldTagName)}`, newTagData),
  getTagStatistics: (params = {}) => apiClient.get('/api/super-admin/tags/stats', { params }),
  getQuestionTags: () => apiClient.get('/api/super-admin/content-generator/questions/tags'),

  // Test Configuration Management
  getAllTestConfigurations: () => apiClient.get('/api/super-admin/test-configurations'),
  getTestConfigurationById: (id) => apiClient.get(`/api/super-admin/test-configurations/${id}`),
  createTestConfiguration: (configData) => apiClient.post('/api/super-admin/test-configurations', configData),
  updateTestConfiguration: (id, configData) => apiClient.put(`/api/super-admin/test-configurations/${id}`, configData),
  deleteTestConfiguration: (id) => apiClient.delete(`/api/super-admin/test-configurations/${id}`),
  updateTestConfigurationStatus: (id, isActive) => apiClient.patch(`/api/super-admin/test-configurations/${id}/status`, { isActive }),
  updateTestConfigurationTenantStatus: (id, tenantId, isActive) => apiClient.patch(`/api/super-admin/test-configurations/${id}/tenant/${tenantId}/status`, { isActive }),
  getTestConfigurationStats: () => apiClient.get('/api/super-admin/test-configurations/stats'),
  validateTestConfigurationQuestions: (configId) => apiClient.post(`/api/super-admin/test-configurations/${configId}/validate`),

  // Analytics
  getTenantComparison: () => apiClient.get('/api/super-admin/analytics/tenant-comparison'),
  getUsageTrends: (params = {}) => apiClient.get('/api/super-admin/analytics/usage-trends', { params }),
  getRevenueAnalytics: () => apiClient.get('/api/super-admin/analytics/revenue'),
  exportAnalytics: (params = {}) => apiClient.get('/api/super-admin/analytics/export', { params }),

  // Password Reset
  validateResetToken: (params) => apiClient.get('/api/auth/validate-reset-token', { params }),
  resetPassword: (data) => apiClient.post('/api/auth/reset-password-with-token', data),
};

// Authentication API
export const authAPI = {
  login: (credentials) => apiClient.post('/api/auth/login', credentials),
  logout: () => apiClient.post('/api/auth/logout'),
  refreshToken: () => apiClient.post('/api/auth/refresh'),
  getCurrentUser: () => apiClient.get('/api/auth/me'),
  validateResetToken: (params) => apiClient.get('/api/auth/validate-reset-token', { params }),
  resetPassword: (data) => apiClient.post('/api/auth/reset-password-with-token', data),
};

// Utility functions
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('authToken', token);
    apiClient.defaults.headers.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem('authToken');
    delete apiClient.defaults.headers.Authorization;
  }
};

export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

export const isAuthenticated = () => {
  return !!getAuthToken();
};

// Error handling utilities
export const handleAPIError = error => apiErrorMessage(error);

// File download utility
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default apiClient;
